import React, { Fragment, useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "@unform/web";
import { useLocation } from "react-router-dom";
import tvgConf from "@tvg/conf";

import { array } from "yup";
import RegularPageRegistration from "@tvg/atomic-ui/_templates/RegularPageRegistration";
import Button from "@tvg/atomic-ui/_atom/Buttons/button";
import { eyeLineShow, eyeLineHide } from "@tvg/atomic-ui/_static/Icons/icons";
import {
  userTick,
  lock,
  file,
  cup
} from "@tvg/atomic-ui/_static/Icons/iconsDuotone";
import { fontMedium } from "@tvg/atomic-ui/_static/Typography";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import InputSSN from "@tvg/atomic-ui/_molecule/InputSSN";
import Icon from "@tvg/atomic-ui/_static/Icons";
import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";
import {
  handleBackButtonHeader,
  handleInputOnBlurGtm,
  handleInputOnTheFlyChange,
  handleShortSSNValidation
} from "../utils/handlers";

import { setCurrentStepRegistrationAction } from "../actions";
import { onBackButtonClickGtmEvent } from "../utils/gtm/otherInteractions";
import { onVirtualPageViewGtmEvent } from "../utils/gtm/pageViews";
import {
  getCurrentStep,
  getIsEditing,
  getRegistrationTextsPage,
  getSsnFields,
  getSsnIsFormValid
} from "../utils/selectors";

export const shortSSNSchema = (ssnTexts) =>
  array()
    .test("check-empty", ssnTexts.errors.empty, (ssn) => ssn.join(""))
    .test(
      "check-fulfilled",
      ssnTexts.errors.incomplete,
      (ssn) => ssn.join("").length === 4
    )
    .test("check-invalid-sequence", ssnTexts.errors.invalidSequence, (ssn) =>
      /^(?!(0000))\d{4}$/.test(ssn.join(""))
    );

const SsnComponent = ({ device }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const ssnTexts = useSelector(getRegistrationTextsPage("ssn"));
  const currentStep = useSelector(getCurrentStep);
  const isFormValid = useSelector(getSsnIsFormValid);
  const ssnFields = useSelector(getSsnFields);
  const isEditing = useSelector(getIsEditing);

  const formRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSSNVisible, setIsSSNVisible] = useState(false);

  useEffect(() => {
    const view = isEditing ? "edit-ssn-details" : "ssn-details";
    onVirtualPageViewGtmEvent(location, view);
  }, []);

  useEffect(() => {
    // Populate welcome fields for editing purposes
    if (ssnFields) {
      formRef.current.setData(ssnFields);
    }
  }, [JSON.stringify(ssnFields)]);

  return (
    <Fragment>
      <RegularPageRegistration
        device={device}
        brand={tvgConf().brand}
        innerTitle={isEditing ? "Edit last 4 SSN" : ssnTexts.innerTitle}
        handleBackButton={() => {
          onBackButtonClickGtmEvent(
            isEditing ? "Edit last 4 SSN" : ssnTexts.innerTitle
          );
          if (isEditing) {
            dispatch(
              setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW)
            );
          } else {
            handleBackButtonHeader({
              dispatch,
              currentStep
            });
          }
        }}
        infoTitle={ssnTexts.innerDescriptionTitle}
        infoText={[
          {
            status: "COMMON",
            text: ssnTexts.innerDescription[0],
            icon: userTick,
            type: "userTick",
            colorList: [
              buildColor("grey", "100"),
              buildColor("grey", "100"),
              buildColor("grey", "100")
            ],
            strokeColorList: [null, null, buildColor("grey", "700")],
            strokeWidthList: [0, 0, 1],
            viewBoxSize: 16
          },
          // the icon below is new and it was exported with a 32 viewBoxSize
          {
            status: "COMMON",
            text: ssnTexts.innerDescription[1],
            icon: cup,
            type: "cup",
            color: buildColor("grey", "100"),
            viewBoxSize: 32,
            noMarginBottomLastChild: false
          }
        ]}
        infoTitleLast4={ssnTexts.innerDescriptionTitle2}
        infoTextLast4={[
          {
            status: "COMMON",
            text: ssnTexts.innerDescription2[0],
            icon: lock,
            type: "lock",
            colorList: [null, buildColor("grey", "100"), null],
            strokeColorList: [
              buildColor("grey", "700"),
              buildColor("grey", "700"),
              buildColor("grey", "700")
            ],
            strokeWidthList: [1, 1, 1],
            viewBoxSize: 16
          },
          {
            status: "COMMON",
            text: ssnTexts.innerDescription2[1],
            icon: file,
            type: "file",
            colorList: [
              buildColor("grey", "100"),
              buildColor("grey", "100"),
              buildColor("grey", "100")
            ],
            strokeColorList: [
              null,
              buildColor("grey", "700"),
              buildColor("grey", "700")
            ],
            strokeWidthList: [null, 1, 1],
            viewBoxSize: 16
          }
        ]}
        currentStep={isEditing ? REGISTRATION_STEPS.REVIEW : currentStep}
        isEditing={isEditing}
        isSsn
        msgBoxTitle={ssnTexts.msgBoxTitle}
      >
        <Form
          ref={formRef}
          onSubmit={handleShortSSNValidation({
            dispatch,
            formRef,
            isFormValid,
            setIsFormDirty,
            ssnSchema: shortSSNSchema(ssnTexts),
            isEditing,
            ssnTexts
          })}
        >
          <InputSSN
            name="ssn"
            label={ssnTexts.inputLabel}
            isVisible={isSSNVisible}
            icon={
              <Icon
                icon={isSSNVisible ? eyeLineHide : eyeLineShow}
                size={16}
                viewBoxSize={16}
                color={buildColor("blue_accent", "500")}
              />
            }
            handleIconClick={() => setIsSSNVisible(!isSSNVisible)}
            debouncedValueCallback={(val) => {
              const ssnArray = val.split("");

              handleInputOnBlurGtm(
                "ssn",
                shortSSNSchema(ssnTexts),
                isEditing,
                ssnTexts.innerTitle
              )(ssnArray);
            }}
            customHandleChange={(value) => {
              handleInputOnTheFlyChange(
                isFormDirty,
                "ssn",
                value,
                shortSSNSchema(ssnTexts),
                formRef,
                ssnTexts.innerTitle,
                isEditing
              );
            }}
            align="center"
            size="large"
            marginTop={20}
            marginBottom={24}
            borderTop={1}
            paddingTop={20}
            autoFocus
          />
          <Button
            size="bigger"
            hasRoundedCorners
            isStretched
            isUppercase={false}
            hasShadow
            fontFamily={fontMedium}
            isBold={false}
            qaLabel="registration-submit-btn"
          >
            Continue and review details
          </Button>
        </Form>
      </RegularPageRegistration>
    </Fragment>
  );
};

export default SsnComponent;
